/**
 * v3.lobby — WAVE-9 Lobby Queue client.
 *
 * Backed by Functions/LobbyFunction.cs. Five client surfaces share
 * these endpoints (Queue, Entry-Internal, Entry-External, Entry-Kiosk,
 * Management).
 */

import type {
  RequestOptions,
  LobbyTicket,
  LobbyReason,
  LobbyCheckInInput,
  LobbyClaimInput,
  LobbyCompleteInput,
  LobbyNoteInput,
  LobbyManagementSnapshot,
  LobbyReasonUpsertInput,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface LobbyTicketResponse {
  success: boolean;
  ticket?: LobbyTicket;
  noChange?: boolean;
  error?: string;
  errorCode?: string;
}

export interface LobbyTicketCreateResponse {
  success: boolean;
  ticketId?: string;
  ticketNumber?: string;
  arrivedAt?: string;
  ticket?: LobbyTicket;
  error?: string;
}

export interface LobbyQueueListResponse {
  success: boolean;
  count: number;
  tickets: ReadonlyArray<LobbyTicket>;
}

export interface LobbyManagementResponse extends LobbyManagementSnapshot {
  success: boolean;
}

export interface LobbyReasonsListResponse {
  success: boolean;
  reasons: ReadonlyArray<LobbyReason>;
}

export interface LobbyAdminUpsertResponse {
  success: boolean;
  code?: string;
  error?: string;
}

export class LobbyApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public checkIn(body: LobbyCheckInInput, opts?: RequestOptions): Promise<LobbyTicketCreateResponse> {
    return this.http.post<LobbyTicketCreateResponse>('/v3/lobby/check-in', { ...opts, body });
  }

  public checkInSelf(body: LobbyCheckInInput, opts?: RequestOptions): Promise<LobbyTicketCreateResponse> {
    return this.http.post<LobbyTicketCreateResponse>('/v3/lobby/check-in-self', { ...opts, body });
  }

  public queue(
    filters: { branch?: string; status?: string; top?: number } = {},
    opts?: RequestOptions,
  ): Promise<LobbyQueueListResponse> {
    return this.http.get<LobbyQueueListResponse>('/v3/lobby/queue', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(ticketId: string, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.get<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}`, { ...opts });
  }

  public claim(ticketId: string, body: LobbyClaimInput, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.post<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}/claim`, { ...opts, body });
  }

  public startService(ticketId: string, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.post<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}/start-service`, { ...opts, body: {} });
  }

  public complete(ticketId: string, body: LobbyCompleteInput, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.post<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}/complete`, { ...opts, body });
  }

  public noShow(ticketId: string, body: LobbyNoteInput, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.post<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}/no-show`, { ...opts, body });
  }

  public cancel(ticketId: string, body: LobbyNoteInput, opts?: RequestOptions): Promise<LobbyTicketResponse> {
    return this.http.post<LobbyTicketResponse>(`/v3/lobby/tickets/${encodeURIComponent(ticketId)}/cancel`, { ...opts, body });
  }

  public management(filters: { branch?: string } = {}, opts?: RequestOptions): Promise<LobbyManagementResponse> {
    return this.http.get<LobbyManagementResponse>('/v3/lobby/management', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public publicReasons(opts?: RequestOptions): Promise<LobbyReasonsListResponse> {
    return this.http.get<LobbyReasonsListResponse>('/v3/lobby/reasons', { ...opts });
  }

  public adminReasonsList(opts?: RequestOptions): Promise<LobbyReasonsListResponse> {
    return this.http.get<LobbyReasonsListResponse>('/v3/admin/lobby/reasons', { ...opts });
  }

  public adminReasonsUpsert(
    bypassKey: string,
    body: LobbyReasonUpsertInput,
    opts?: RequestOptions,
  ): Promise<LobbyAdminUpsertResponse> {
    const headers = { ...(opts?.headers ?? {}), 'x-config-seed-bypass': bypassKey };
    return this.http.post<LobbyAdminUpsertResponse>('/v3/admin/lobby/reasons', { ...opts, headers, body });
  }
}
